import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { getLocale, useIntl } from 'umi';
import { useCopilotLeftMenuCollapsed } from '@/pages/aigc/pages/Agent/hooks/useCopilotLeftMenuCollapsed';
import { useCopilotOpenBridge } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import { ThemeContext } from '@/pages/home/wrappers/common';
import { buildAIGalleryReportEditConfig } from '@/pages/home/pages/AIGalleryReport/components/ReportToolbar/hooks/buildAIGalleryReportEditConfig';
import {
  SYSTEM_THEME_DARK,
  LANGUAGE_CN,
} from '@/pages/home/_config/config';
import './index.scss';

/**
 * AI Gallery 嵌入页
 *
 * 与子页面 (/ai-gallery) 的对接协议：
 * 1) 初始通过 URL query 传递：?theme=light|dark&lang=zh|en
 *    子页面读取示例：
 *      const qs = location.search.match(/[?&]theme=(light|dark)/);
 *      const langQs = location.search.match(/[?&]lang=(zh|en)/);
 * 2) 主题 / 语言 / Copilot 内嵌侧栏态通过 postMessage 通知，消息体：
 *      { source: 'databrain-main-frame', type: 'theme', value: 'light' | 'dark' }
 *      { source: 'databrain-main-frame', type: 'lang',  value: 'zh'    | 'en'   }
 *      { source: 'databrain-main-frame', type: 'sidebarCollapsed', value: true | false }
 *    侧栏无 URL 初值：子应用上行 ai-gallery:ready 后再补发一次 sidebarCollapsed
 *    子页面订阅示例：
 *      window.addEventListener('message', (e) => {
 *        if (e.data?.source !== 'databrain-main-frame') return;
 *        if (e.data.type === 'theme') applyTheme(e.data.value);
 *        if (e.data.type === 'lang')  applyLang(e.data.value);
 *        if (e.data.type === 'sidebarCollapsed') setSidebarCollapsed(e.data.value);
 *      });
 */
const AI_GALLERY_PATH = '/ai-gallery';

const AI_GALLERY_TARGET_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : '*';

/** 子应用上行的「AI 编辑 HTML」消息类型 */
const AI_GALLERY_AI_EDIT_MESSAGE = 'ai-gallery:ai-edit';

/** 子应用上行「AI 编辑 HTML」消息 payload */
interface IAiGalleryAiEditMessage {
  /** 报告相对路径（/as/report/...） */
  path: string;
  /** 报告页面名（URL query name，作为 Copilot 引用来源的文件标签） */
  name: string;
}

const themeToParam = (v: string): 'light' | 'dark' =>
  v === SYSTEM_THEME_DARK ? 'dark' : 'light';

const langToParam = (v: string): 'zh' | 'en' =>
  v === LANGUAGE_CN ? 'zh' : 'en';

const AIGalleryHome: React.FC = () => {
  const { formatMessage } = useIntl();
  const { themeValue } = useContext(ThemeContext);
  const lang = langToParam(getLocale());
  const theme = themeToParam(themeValue);

  const { openCopilotWithDefaultAgent } = useCopilotOpenBridge();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [leftMenuCollapsed] = useCopilotLeftMenuCollapsed();

  // iframe src 仅在挂载时计算一次（携带初始 theme/lang），后续变更通过 postMessage
  const initialSrc = useMemo(() => {
    const initialTheme = themeToParam(themeValue);
    const initialLang = langToParam(getLocale());
    return `${AI_GALLERY_PATH}?theme=${initialTheme}&lang=${initialLang}`;
    // 故意空依赖：保证 iframe 不被 src 变化触发重载
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'theme', value: theme },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [theme]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'lang', value: lang },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [lang]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        source: 'databrain-main-frame',
        type: 'sidebarCollapsed',
        value: leftMenuCollapsed,
      },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [leftMenuCollapsed]);

  // 监听子应用上行：ready 时补发侧栏收起；ai-edit 拉起 Copilot
  useEffect(() => {
    const handleGalleryMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      // 侧栏无 URL 初值，iframe 监听就绪前下发会丢失，ready 后按 theme/lang 同款补发一次
      if (event.data?.type === 'ai-gallery:ready') {
        iframeRef.current?.contentWindow?.postMessage(
          {
            source: 'databrain-main-frame',
            type: 'sidebarCollapsed',
            value: leftMenuCollapsed,
          },
          AI_GALLERY_TARGET_ORIGIN,
        );
        return;
      }

      if (event.data?.type !== AI_GALLERY_AI_EDIT_MESSAGE) return;

      const { path, name } = event.data.payload as IAiGalleryAiEditMessage;
      if (!path) return;

      const config = buildAIGalleryReportEditConfig({
        path,
        pageName: name,
        title: name,
        query: formatMessage({ id: 'dbh_studio_aigallery_report_edit_query' }),
      });
      if (!config) return;
      openCopilotWithDefaultAgent(config);
    };

    window.addEventListener('message', handleGalleryMessage);
    return () => window.removeEventListener('message', handleGalleryMessage);
  }, [formatMessage, openCopilotWithDefaultAgent, leftMenuCollapsed]);

  return (
    <div className="dbh-aigallery-home">
      <iframe
        ref={iframeRef}
        title="ai-gallery"
        className="dbh-aigallery-home__iframe"
        src={initialSrc}
      />
    </div>
  );
};

export default AIGalleryHome;
